import React, { useState, useEffect } from 'react';
import { getState, setState, subscribe, useInsuranceLabel } from '../lib/userStore';
import { saveCareOutcome } from '../lib/triage/triageService';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / (1000 * 60 * 60));
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ─── Static data ──────────────────────────────────────────────────────────────
const COPAYS = {
  bcbs: {
    ppo:  { primary: '$30', specialist: '$60', urgent: '$75', er: '$350', rx: '$10' },
    hmo:  { primary: '$20', specialist: '$45', urgent: '$60', er: '$300', rx: '$10' },
    hdhp: { primary: '$0*', specialist: '$0*', urgent: '$0*', er: '$0*',  rx: '$15' },
    epo:  { primary: '$25', specialist: '$55', urgent: '$70', er: '$350', rx: '$10' },
  },
  aetna: {
    ppo:  { primary: '$35', specialist: '$65', urgent: '$80', er: '$400', rx: '$15' },
    hmo:  { primary: '$25', specialist: '$50', urgent: '$65', er: '$325', rx: '$10' },
    hdhp: { primary: '$0*', specialist: '$0*', urgent: '$0*', er: '$0*',  rx: '$15' },
    epo:  { primary: '$30', specialist: '$60', urgent: '$75', er: '$375', rx: '$12' },
  },
  medicaid: {
    ppo:  { primary: '$0', specialist: '$3', urgent: '$3', er: '$8', rx: '$1' },
    hmo:  { primary: '$0', specialist: '$3', urgent: '$3', er: '$8', rx: '$1' },
    hdhp: { primary: '$0', specialist: '$3', urgent: '$3', er: '$8', rx: '$1' },
    epo:  { primary: '$0', specialist: '$3', urgent: '$3', er: '$8', rx: '$1' },
  },
};

const CARRIER_NAMES = {
  bcbs: 'BlueCross BlueShield', aetna: 'Aetna', cigna: 'Cigna',
  united: 'UnitedHealth', humana: 'Humana', medicaid: 'SC Medicaid', medicare: 'Medicare',
};
const PLAN_NAMES = { ppo: 'PPO', hmo: 'HMO', hdhp: 'HDHP', epo: 'EPO' };

function greeting(firstName) {
  const h = new Date().getHours();
  const time = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  return firstName ? `Good ${time}, ${firstName}.` : `Good ${time}.`;
}

// ─── SVG icons ────────────────────────────────────────────────────────────────
const SymptomSVG = ({ color }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={color}/>
  </svg>
);
const FindCareSVG = ({ color }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="9" cy="7" r="4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const InsuranceSVG = ({ color }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
    <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" fill={color}/>
  </svg>
);
const PreventativeSVG = ({ color }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ProfileSVG = ({ color }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Contextual tile config ───────────────────────────────────────────────────
function getContextualTiles(st) {
  const hasInsurance = !!(st.carrier && st.planType);
  return [
    { id: 'getCare',      label: 'Get Care Now',  bg: 'var(--teal-faint)',  color: 'var(--teal)',   icon: c => <SymptomSVG color={c} /> },
    { id: 'findCare',     label: 'Find Care',      bg: 'var(--teal-faint)',  color: 'var(--teal)',   icon: c => <FindCareSVG color={c} /> },
    { id: 'preventative', label: 'Preventative',   bg: 'var(--teal-faint)',  color: 'var(--teal)',   icon: c => <PreventativeSVG color={c} /> },
    hasInsurance
      ? { id: 'profile',   label: 'My Profile',    bg: 'var(--cream-2)',     color: 'var(--ink-3)',  icon: c => <ProfileSVG color={c} /> }
      : { id: 'insurance', label: 'Add Coverage',  bg: 'var(--amber-light)', color: 'var(--amber)', icon: c => <InsuranceSVG color={c} /> },
  ];
}

// ─── Anonymous home ───────────────────────────────────────────────────────────
function AnonHome({ navigate, onAuthPrompt }) {
  const insuranceLabel = useInsuranceLabel();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Double rAF ensures initial paint happens before transition fires
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
    return () => cancelAnimationFrame(raf);
  }, []);

  const cards = [
    {
      id: 'getCare',
      title: 'I have a symptom',
      desc: 'Know if you need urgent care, a doctor visit, or can wait',
      accent: 'var(--teal)',
      icon: <SymptomSVG color="var(--teal)" />,
    },
    {
      id: 'findCare',
      title: 'Find care in Greenville',
      desc: 'Doctors, urgent care, free clinics, mental health & more',
      accent: 'var(--teal)',
      icon: <FindCareSVG color="var(--teal)" />,
    },
    {
      id: 'insurance',
      title: 'Understand my coverage',
      desc: insuranceLabel
        ? `Viewing: ${insuranceLabel}`
        : "Plain-English breakdown of what you'll actually pay",
      accent: 'var(--amber)',
      icon: <InsuranceSVG color="var(--amber)" />,
    },
  ];

  return (
    <div style={sa.screen}>
      {/* Header — wordmark + sign-in + location */}
      <div style={sa.header}>
        <div style={sa.headerRow}>
          <div style={sa.wordmark}>
            <span style={sa.wordmarkBold}>Clear</span>
            <span style={sa.wordmarkLight}>path</span>
          </div>
          <button style={sa.signInBtn} onClick={onAuthPrompt}>Sign in</button>
        </div>
        <div style={sa.location}>
          <svg width="9" height="11" viewBox="0 0 9 11" fill="none" style={{ flexShrink: 0 }}>
            <path d="M4.5 0C2.01 0 0 2.01 0 4.5c0 3.375 4.5 6.5 4.5 6.5s4.5-3.125 4.5-6.5C9 2.01 6.99 0 4.5 0zm0 6.125A1.625 1.625 0 114.5 2.875 1.625 1.625 0 014.5 6.125z" fill="currentColor"/>
          </svg>
          Greenville, SC
        </div>
      </div>

      {/* Scrollable body */}
      <div style={sa.body}>
        <div style={sa.bodyInner}>
          {/* Hero */}
          <div style={{
            ...sa.hero,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'none' : 'translateY(10px)',
            transition: 'opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            <h1 style={sa.h1}>
              What do you<br />
              <em style={sa.h1Em}>need right now?</em>
            </h1>
            <p style={sa.subtitle}>Your local health guide — built for Greenville families.</p>
          </div>

          {/* Action cards */}
          <div style={sa.cards}>
            {cards.map((card, i) => (
              <button
                key={card.id}
                style={{
                  ...sa.card,
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(18px)',
                  transition: `opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1) ${130 + i * 80}ms, transform 0.45s cubic-bezier(0.16, 1, 0.3, 1) ${130 + i * 80}ms`,
                }}
                onClick={() => navigate(card.id)}
              >
                <div style={{ ...sa.cardIcon, background: card.accent === 'var(--amber)' ? 'var(--amber-light)' : 'var(--teal-faint)', borderRadius: '10px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{card.icon}</div>
                <div style={sa.cardText}>
                  <div style={sa.cardTitle}>{card.title}</div>
                  <div style={sa.cardDesc}>{card.desc}</div>
                </div>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0, color: 'var(--ink-4)' }}>
                  <path d="M5.5 2.5l5 5-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
          </div>

          <div style={{ height: 32 }} />
        </div>
      </div>
    </div>
  );
}

// ─── Signed-in home ───────────────────────────────────────────────────────────
function SignedInHome({ navigate, user }) {
  const [st, setSt] = useState(getState());

  useEffect(() => subscribe(s => setSt({ ...s })), []);

  const { firstName, carrier, planType, recentSymptomLevel, recentSymptomAt, recentTriageSessionId } = st;
  const carrierLabel = carrier ? (CARRIER_NAMES[carrier] || carrier) : null;
  const planLabel    = planType ? (PLAN_NAMES[planType] || planType) : null;
  const copays       = carrier && planType
    ? ((COPAYS[carrier] || COPAYS.bcbs)[planType] || COPAYS.bcbs.ppo)
    : null;
  const showNudge = !!recentSymptomLevel;

  function dismissNudge(didSeekCare) {
    saveCareOutcome(recentTriageSessionId, user?.id || null, didSeekCare);
    setState({ recentSymptomLevel: null, recentSymptomAt: null, recentTriageSessionId: null });
  }

  const nudgeCfg = {
    urgent: { bg: 'var(--amber-light)', border: 'rgba(196,114,42,0.2)',  accent: 'var(--amber)' },
    soon:   { bg: 'var(--teal-faint)',  border: 'rgba(30,92,84,0.15)',   accent: 'var(--teal)'  },
    home:   { bg: 'var(--cream-2)',     border: 'var(--border)',          accent: 'var(--ink-3)' },
  };
  const nudgeCf = (recentSymptomLevel && nudgeCfg[recentSymptomLevel]) || nudgeCfg.soon;
  const nudgeQuestion = {
    urgent: 'How are you feeling?',
    soon:   'Did you get the care you needed?',
    home:   'Feeling any better?',
  }[recentSymptomLevel] || 'How are you feeling?';

  const tiles = getContextualTiles(st);
  // Key changes when tile IDs change → remounts grid → CSS animation replays
  const tileKey = tiles.map(t => t.id).join('-');

  return (
    <div style={sh.screen}>
      {/* Teal header */}
      <div style={sh.header}>
        <div style={sh.wordmark}>
          <span style={sh.wordmarkBold}>Clear</span>
          <span style={sh.wordmarkItalic}>path</span>
        </div>
        <div style={sh.greeting}>{greeting(firstName)}</div>
        {carrierLabel && planLabel ? (
          <div style={sh.planBadge}>
            <InsuranceSVG color="rgba(255,255,255,0.7)" />
            {carrierLabel} · {planLabel}
          </div>
        ) : (
          <button style={sh.addCoverageBtn} onClick={() => navigate('insurance')}>
            Add your insurance →
          </button>
        )}
      </div>

      <div style={sh.body}>
        <div style={sh.bodyInner}>

          {/* 48-hr follow-up nudge */}
          {showNudge && (
            <div style={{ ...sh.nudge, background: nudgeCf.bg, border: `1px solid ${nudgeCf.border}` }}>
              <div style={sh.nudgeHeader}>
                <span style={{ ...sh.nudgeCheckin, color: nudgeCf.accent }}>Checking in</span>
                <span style={sh.nudgeTime}>{timeAgo(recentSymptomAt)}</span>
              </div>
              <div style={sh.nudgeQuestion}><em>{nudgeQuestion}</em></div>
              <div style={sh.nudgeBtns}>
                <button
                  style={sh.nudgePrimary}
                  onClick={() => dismissNudge(true)}
                >
                  I got the care I needed
                </button>
                <button
                  style={sh.nudgeLink}
                  onClick={() => { dismissNudge(false); navigate('getCare'); }}
                >
                  Check symptoms again →
                </button>
              </div>
            </div>
          )}

          {/* Coverage — onboarding card for new users, full view for returning */}
          {!copays ? (
            <div style={sh.onboardCard}>
              <div style={sh.onboardTitle}>Get your coverage at a glance</div>
              <div style={sh.onboardDesc}>
                Add your insurance once and we'll show exactly what your copays are — no billing jargon.
              </div>
              <button style={sh.onboardCta} onClick={() => navigate('insurance')}>
                Add insurance →
              </button>
            </div>
          ) : (
            <div style={sh.coverageCard}>
              <div style={sh.coverageHeader}>
                <div>
                  <div style={sh.coverageLabel}>Your coverage</div>
                  <div style={sh.coverageTitle}>{carrierLabel} {planLabel}</div>
                </div>
                <button style={sh.coverageEditBtn} onClick={() => navigate('insurance')}>Edit</button>
              </div>
              <div>
                {[
                  { name: 'Primary care', val: copays.primary,    color: 'var(--teal)'  },
                  { name: 'Specialist',   val: copays.specialist,  color: 'var(--ink)'   },
                  { name: 'Urgent care',  val: copays.urgent,      color: 'var(--ink)'   },
                  { name: 'ER visit',     val: copays.er,          color: 'var(--amber)' },
                  { name: 'Rx (generic)', val: copays.rx,          color: 'var(--teal)'  },
                ].map(row => (
                  <div key={row.name} style={sh.copayRow}>
                    <span style={sh.copayName}>{row.name}</span>
                    <span style={{ ...sh.copayVal, color: row.color }}>{row.val}</span>
                  </div>
                ))}
                {planType === 'hdhp' && (
                  <div style={sh.hdhpNote}>
                    * $0 copay applies <strong>after</strong> your deductible is met. HDHP plans are HSA-eligible.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contextual quick-access grid */}
          <div style={sh.gridLabel}>Quick access</div>
          <div key={tileKey} style={sh.grid}>
            {tiles.map((tile, idx) => (
              <button
                key={tile.id}
                style={{
                  ...sh.tile,
                  animation: `tileFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 55}ms both`,
                }}
                onClick={() => navigate(tile.id)}
              >
                <div style={{ ...sh.tileIcon, background: tile.bg }}>
                  {tile.icon(tile.color)}
                </div>
                <div style={{ ...sh.tileLabel, color: tile.color }}>{tile.label}</div>
              </button>
            ))}
          </div>

          <div style={{ height: 16 }} />
        </div>
      </div>
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────
export default function HomeScreen(props) {
  return props.user ? <SignedInHome {...props} /> : <AnonHome {...props} />;
}

// ─── Signed-in styles ─────────────────────────────────────────────────────────
const sh = {
  screen: {
    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--cream)',
  },
  header: {
    padding: '52px 24px 22px', background: 'var(--teal)', flexShrink: 0,
  },
  wordmark: {
    display: 'flex', alignItems: 'baseline', gap: '1px', marginBottom: '12px', opacity: 0.6,
  },
  wordmarkBold: {
    fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '15px', color: 'white', letterSpacing: '-0.2px',
  },
  wordmarkItalic: {
    fontFamily: 'var(--font-display)', fontWeight: 400, fontStyle: 'italic', fontSize: '15px', color: 'white',
  },
  greeting: {
    fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '26px', color: 'white',
    letterSpacing: '-0.3px', lineHeight: 1.2, marginBottom: '10px',
  },
  planBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.15)', borderRadius: '100px',
    padding: '5px 12px', fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 500,
  },
  addCoverageBtn: {
    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '100px',
    padding: '6px 14px', fontSize: '12.5px', color: 'white',
    fontFamily: 'var(--font-body)', fontWeight: 500, cursor: 'pointer',
  },
  body: {
    flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
  },
  bodyInner: {
    display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px 20px 0',
  },
  nudge: {
    borderRadius: 'var(--radius-lg)', padding: '16px 18px',
  },
  nudgeHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px',
  },
  nudgeCheckin: {
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  },
  nudgeTime: {
    fontSize: '11px', color: 'var(--ink-4)', fontWeight: 500,
  },
  nudgeQuestion: {
    fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--ink)',
    letterSpacing: '-0.2px', lineHeight: 1.25, marginBottom: '16px',
  },
  nudgeBtns: { display: 'flex', flexDirection: 'column', gap: '10px' },
  nudgePrimary: {
    width: '100%', padding: '12px', background: 'var(--teal)', color: 'white',
    border: 'none', borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
  nudgeLink: {
    width: '100%', padding: '4px', background: 'none', border: 'none',
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 500,
    color: 'var(--ink-3)', cursor: 'pointer', textAlign: 'center',
  },
  onboardCard: {
    background: 'var(--amber-light)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(196,114,42,0.3)',
    padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  onboardTitle: {
    fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '17px',
    color: 'var(--ink)', letterSpacing: '-0.2px', lineHeight: 1.3,
  },
  onboardDesc: {
    fontSize: '13px', color: 'var(--ink-2)', lineHeight: 1.5,
  },
  onboardCta: {
    alignSelf: 'flex-start', padding: '10px 18px', background: 'var(--amber)',
    color: 'white', border: 'none', borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
  },
  coverageCard: {
    background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)', overflow: 'hidden',
  },
  coverageHeader: {
    padding: '14px 18px', background: 'var(--amber-light)',
    borderBottom: '1px solid rgba(196,114,42,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  coverageLabel: {
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '2px',
  },
  coverageTitle: {
    fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '15px', color: 'var(--ink)',
  },
  coverageEditBtn: {
    fontSize: '12px', fontWeight: 600, color: 'var(--amber)',
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-body)', padding: '4px 8px',
  },
  copayRow: {
    padding: '10px 18px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', borderBottom: '1px solid var(--border)',
  },
  copayName: { fontSize: '13px', color: 'var(--ink-2)' },
  copayVal: {
    fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '16px',
    letterSpacing: '-0.2px', fontVariantNumeric: 'tabular-nums',
  },
  hdhpNote: {
    padding: '10px 18px 12px', fontSize: '11.5px', color: 'var(--ink-4)', lineHeight: 1.5,
  },
  gridLabel: {
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: '-4px',
  },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  tile: {
    background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)', padding: '18px 14px',
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px',
    cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left',
    WebkitTapHighlightColor: 'transparent',
  },
  tileIcon: {
    width: '38px', height: '38px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  tileLabel: { fontSize: '13px', fontWeight: 600, lineHeight: 1.2 },
};

// ─── Anonymous styles ─────────────────────────────────────────────────────────
const sa = {
  screen: {
    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
    overflow: 'hidden', background: 'var(--cream)',
  },
  header: { padding: '56px 24px 18px', flexShrink: 0 },
  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px',
  },
  wordmark: { display: 'flex', alignItems: 'baseline', gap: '1px' },
  wordmarkBold: {
    fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '22px',
    color: 'var(--teal)', letterSpacing: '-0.5px',
  },
  wordmarkLight: {
    fontFamily: 'var(--font-display)', fontWeight: 400, fontStyle: 'italic',
    fontSize: '22px', color: 'var(--ink-3)', letterSpacing: '-0.3px',
  },
  signInBtn: {
    fontSize: '12.5px', fontWeight: 600, color: 'var(--teal)',
    background: 'none', border: '1.5px solid rgba(30,92,84,0.25)',
    borderRadius: '100px', padding: '6px 14px', cursor: 'pointer',
    fontFamily: 'var(--font-body)', WebkitTapHighlightColor: 'transparent',
  },
  location: {
    display: 'flex', alignItems: 'center', gap: '5px',
    fontSize: '11px', fontWeight: 500, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: 'var(--ink-4)',
  },
  body: { flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' },
  bodyInner: { display: 'flex', flexDirection: 'column' },
  hero: { padding: '8px 24px 30px' },
  h1: {
    fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '32px',
    lineHeight: 1.15, color: 'var(--ink)', letterSpacing: '-0.5px',
  },
  h1Em: { fontStyle: 'italic', color: 'var(--teal)', fontWeight: 400 },
  subtitle: {
    marginTop: '10px', fontSize: '14px', color: 'var(--ink-3)',
    lineHeight: 1.55, fontWeight: 400,
  },
  cards: { padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  card: {
    background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px',
    padding: '18px 16px 18px 20px', textAlign: 'left', fontFamily: 'var(--font-body)',
    WebkitTapHighlightColor: 'transparent',
  },
  cardIcon: { flexShrink: 0 },
  cardText: { flex: 1, minWidth: 0 },
  cardTitle: {
    fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '18px',
    color: 'var(--ink)', letterSpacing: '-0.2px', lineHeight: 1.2, marginBottom: '3px',
  },
  cardDesc: { fontSize: '12.5px', color: 'var(--ink-3)', lineHeight: 1.45 },
};
